import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { toast } from 'sonner';
import { 
  Heart,
  Clock,
  Plus,
  ShoppingCart,
  Eye,
  Search,
  Loader2,
  CheckSquare,
  X,
  Calendar,
  ChevronRight,
  Sparkles,
  Utensils
} from 'lucide-react';
import { haptic } from '@/utils/haptics';

// Filter options
const FILTERS = [
  { id: 'all', label: 'All', icon: null },
  { id: 'quick', label: 'Quick', icon: '⚡' },
  { id: 'under30', label: 'Under 30 min', icon: '⏱️' },
  { id: 'family', label: 'Family', icon: '👨‍👩‍👧' },
  { id: 'vegetarian', label: 'Vegetarian', icon: '🥗' },
];

// Days for meal planning
const DAYS = [
  { id: 'today', label: 'Today', getDate: () => new Date() },
  { id: 'tomorrow', label: 'Tomorrow', getDate: () => { const d = new Date(); d.setDate(d.getDate() + 1); return d; } },
  { id: 'pick', label: 'Pick a day', getDate: null },
];

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Mobile-optimized Favorites Component
 * Goal: Help users quickly pick something they already like
 */
const MobileFavorites = ({ favorites, loading, onRemoveFavorite, onAddToMealPlan, onAddIngredients }) => {
  const navigate = useNavigate();
  const { api } = useAuth();
  
  // Filter state
  const [activeFilter, setActiveFilter] = useState('all');
  
  // Selection state for bulk actions
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  // Bottom sheet states
  const [addToPlanSheetOpen, setAddToPlanSheetOpen] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [showDayPicker, setShowDayPicker] = useState(false);
  const [addingToPlan, setAddingToPlan] = useState(false);

  // Filter favorites based on active filter
  const filteredFavorites = favorites.filter(fav => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'quick') return fav.ready_in_minutes && fav.ready_in_minutes <= 20;
    if (activeFilter === 'under30') return fav.ready_in_minutes && fav.ready_in_minutes <= 30;
    if (activeFilter === 'family') return fav.servings && fav.servings >= 4;
    if (activeFilter === 'vegetarian') return fav.vegetarian || fav.recipe_title?.toLowerCase().includes('vegetarian');
    return true;
  });

  // Handle filter change
  const handleFilterChange = (filterId) => {
    haptic('light');
    setActiveFilter(filterId);
  };

  // Toggle selection
  const toggleSelection = (recipeId) => {
    haptic('light');
    const newSelected = new Set(selectedIds);
    if (newSelected.has(recipeId)) {
      newSelected.delete(recipeId);
    } else {
      newSelected.add(recipeId);
    }
    setSelectedIds(newSelected);
  };

  // Handle "Add to Plan" tap
  const handleAddToPlanTap = (recipe) => {
    haptic('medium');
    setSelectedRecipe(recipe);
    setShowDayPicker(false);
    setAddToPlanSheetOpen(true);
  };

  // Handle day selection
  const handleDaySelect = async (dayOption) => {
    if (dayOption.id === 'pick') {
      setShowDayPicker(true);
      return;
    }

    await addRecipeToDay(dayOption.getDate());
  };

  // Handle specific day pick
  const handleSpecificDayPick = async (dayIndex) => {
    const today = new Date();
    const targetDate = new Date(today);
    const currentDay = today.getDay();
    const daysToAdd = (dayIndex - currentDay + 7) % 7;
    targetDate.setDate(today.getDate() + daysToAdd);
    
    await addRecipeToDay(targetDate);
  };

  // Add recipe to a specific day
  const addRecipeToDay = async (date) => {
    if (!selectedRecipe) return;
    
    setAddingToPlan(true);
    haptic('medium');
    
    try {
      const dateStr = date.toISOString().split('T')[0];
      
      // First, get the current meal plan
      const currentPlanRes = await api.get('/meal-plans');
      const currentPlan = currentPlanRes.data?.days || [];
      
      // Create the new meal
      const newMeal = {
        recipe_id: selectedRecipe.recipe_id,
        recipe_title: selectedRecipe.recipe_title,
        recipe_image: selectedRecipe.recipe_image,
        servings: 1
      };
      
      // Check if this date already has meals
      const existingDayIndex = currentPlan.findIndex(d => d.date === dateStr);
      let updatedDays;
      
      if (existingDayIndex >= 0) {
        // Add to existing day
        updatedDays = currentPlan.map((day, idx) => 
          idx === existingDayIndex 
            ? { ...day, meals: [...day.meals, newMeal] }
            : day
        );
      } else {
        // Add new day
        updatedDays = [...currentPlan, { date: dateStr, meals: [newMeal] }];
      }
      
      // Save the updated plan
      await api.post('/meal-plans', { days: updatedDays });
      
      toast.success(`Added to ${WEEKDAYS[date.getDay()]}!`, { duration: 2000 });
      haptic('success');
      setAddToPlanSheetOpen(false);
      setSelectedRecipe(null);
    } catch (error) {
      console.error('Failed to add to meal plan:', error);
      toast.error('Failed to add to plan');
    } finally {
      setAddingToPlan(false);
    }
  };

  // Bulk add to plan
  const handleBulkAddToPlan = () => {
    haptic('medium');
    // For bulk, we'll add to the meal planner page
    const selectedRecipes = favorites.filter(f => selectedIds.has(f.recipe_id));
    if (selectedRecipes.length === 0) return;
    
    // Navigate to meal planner with selected recipes
    toast.success(`Opening meal planner with ${selectedRecipes.length} recipes...`);
    navigate('/meal-planner', { state: { suggestedRecipes: selectedRecipes } });
  };

  // Handle add ingredients
  const handleAddIngredients = async (recipe, e) => {
    e?.stopPropagation();
    haptic('medium');
    
    try {
      // Get recipe details with ingredients
      const response = await api.get(`/recipes/${recipe.recipe_id}`);
      const recipeData = response.data;
      
      if (recipeData.extendedIngredients && recipeData.extendedIngredients.length > 0) {
        // Add to most recent grocery list or create new one
        const listsResponse = await api.get('/grocery-lists');
        let targetListId;
        
        if (listsResponse.data.length > 0) {
          targetListId = listsResponse.data[0].id;
        } else {
          const newListResponse = await api.post('/grocery-lists', {
            name: 'My Grocery List',
            items: []
          });
          targetListId = newListResponse.data.id;
        }
        
        // Add ingredients to list
        const existingList = await api.get(`/grocery-lists/${targetListId}`);
        const newItems = recipeData.extendedIngredients.map(ing => ({
          name: ing.name || ing.original,
          amount: ing.amount || 1,
          unit: ing.unit || '',
          aisle: ing.aisle || 'Other',
          checked: false
        }));
        
        await api.put(`/grocery-lists/${targetListId}`, {
          name: existingList.data.name,
          items: [...existingList.data.items, ...newItems]
        });
        
        toast.success(`Added ${newItems.length} ingredients to grocery list!`);
        haptic('success');
      } else {
        toast.info('No ingredients found for this recipe');
      }
    } catch (error) {
      console.error('Failed to add ingredients:', error);
      toast.error('Failed to add ingredients');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" data-testid="mobile-favorites">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b px-4 py-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-xl font-semibold font-['Playfair_Display']">
              My Favorites
            </h1>
            <p className="text-sm text-muted-foreground">
              Quickly add your favorite meals to your plan or grocery list.
            </p>
          </div>
          
          {/* Select Toggle */}
          {favorites.length > 0 && (
            <Button
              variant={selectMode ? "default" : "ghost"}
              size="sm"
              onClick={() => {
                haptic('light');
                setSelectMode(!selectMode);
                setSelectedIds(new Set());
              }}
              data-testid="select-toggle-btn"
            >
              {selectMode ? <X className="h-4 w-4" /> : <CheckSquare className="h-4 w-4" />}
            </Button>
          )}
        </div>

        {/* Filter Chips */}
        {favorites.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
            {FILTERS.map((filter) => (
              <Button
                key={filter.id}
                variant={activeFilter === filter.id ? "default" : "outline"}
                size="sm"
                className={`flex-shrink-0 h-8 px-3 rounded-full ${
                  activeFilter === filter.id ? '' : 'bg-background'
                }`}
                onClick={() => handleFilterChange(filter.id)}
                data-testid={`filter-${filter.id}`}
              >
                {filter.icon && <span className="mr-1">{filter.icon}</span>}
                {filter.label}
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* Favorites List */}
      <div className="px-4 py-4">
        {filteredFavorites.length > 0 ? (
          <div className="space-y-3">
            {filteredFavorites.map((fav) => (
              <FavoriteCard
                key={fav.id}
                favorite={fav}
                selectMode={selectMode}
                isSelected={selectedIds.has(fav.recipe_id)}
                onSelect={() => toggleSelection(fav.recipe_id)}
                onAddToPlan={() => handleAddToPlanTap(fav)}
                onAddIngredients={(e) => handleAddIngredients(fav, e)}
                onView={() => navigate(`/recipes/${fav.recipe_id}`)}
              />
            ))}
          </div>
        ) : favorites.length > 0 ? (
          // No results for current filter
          <div className="text-center py-12">
            <Utensils className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">No recipes match this filter</p>
            <Button variant="outline" onClick={() => setActiveFilter('all')}>
              Show all favorites
            </Button>
          </div>
        ) : (
          // Empty state - no favorites at all
          <div className="text-center py-12" data-testid="empty-favorites">
            <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Heart className="h-10 w-10 text-primary/50" />
            </div>
            <h3 className="text-lg font-semibold font-['Playfair_Display'] mb-2">
              Save recipes you love
            </h3>
            <p className="text-muted-foreground mb-6 max-w-xs mx-auto">
              Tap the heart on any recipe to keep it here for quick planning.
            </p>
            <Button onClick={() => navigate('/recipes')} data-testid="browse-recipes-btn">
              <Search className="h-4 w-4 mr-2" />
              Browse Recipes
            </Button>
          </div>
        )}
      </div>

      {/* Bulk Action Bar */}
      {selectMode && selectedIds.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 p-4 pb-8 bg-background/95 backdrop-blur-sm border-t z-50">
          <Button
            className="w-full h-14 text-base"
            onClick={handleBulkAddToPlan}
            data-testid="bulk-add-btn"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add {selectedIds.size} {selectedIds.size === 1 ? 'meal' : 'meals'} to plan
          </Button>
        </div>
      )}

      {/* Add to Plan Bottom Sheet */}
      <Sheet open={addToPlanSheetOpen} onOpenChange={setAddToPlanSheetOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl pb-8">
          <SheetHeader className="text-left pb-4">
            <SheetTitle className="text-xl font-['Playfair_Display']">
              Add to your week
            </SheetTitle>
            {selectedRecipe && (
              <SheetDescription className="flex items-center gap-2">
                <img 
                  src={selectedRecipe.recipe_image} 
                  alt="" 
                  className="h-8 w-8 rounded object-cover"
                />
                {selectedRecipe.recipe_title}
              </SheetDescription>
            )}
          </SheetHeader>

          {!showDayPicker ? (
            // Quick day options
            <div className="space-y-3">
              {DAYS.map((day) => (
                <Button
                  key={day.id}
                  variant="outline"
                  className="w-full h-14 justify-between text-left active:scale-[0.98]"
                  onClick={() => handleDaySelect(day)}
                  disabled={addingToPlan}
                  data-testid={`day-option-${day.id}`}
                >
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-muted-foreground" />
                    <span className="font-medium">{day.label}</span>
                  </div>
                  {addingToPlan ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  )}
                </Button>
              ))}
            </div>
          ) : (
            // Full week picker
            <div className="space-y-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDayPicker(false)}
                className="mb-2"
              >
                ← Back
              </Button>
              <div className="grid grid-cols-2 gap-2">
                {WEEKDAYS.map((day, index) => (
                  <Button
                    key={day}
                    variant="outline"
                    className="h-12 active:scale-[0.98]"
                    onClick={() => handleSpecificDayPick(index)}
                    disabled={addingToPlan}
                    data-testid={`weekday-${day.toLowerCase()}`}
                  >
                    {day}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

/**
 * Compact Favorite Card Component
 */
const FavoriteCard = ({ 
  favorite, 
  selectMode, 
  isSelected, 
  onSelect, 
  onAddToPlan, 
  onAddIngredients,
  onView 
}) => {
  return (
    <div 
      className={`flex items-center gap-3 p-3 rounded-xl bg-card border transition-all active:scale-[0.99] ${
        isSelected ? 'border-primary ring-1 ring-primary' : 'border-border/50'
      }`}
      onClick={selectMode ? onSelect : undefined}
      data-testid={`favorite-card-${favorite.recipe_id}`}
    >
      {/* Selection Checkbox */}
      {selectMode && (
        <Checkbox
          checked={isSelected}
          onCheckedChange={onSelect}
          className="flex-shrink-0"
        />
      )}

      {/* Thumbnail */}
      <div className="relative flex-shrink-0">
        <img
          src={favorite.recipe_image}
          alt={favorite.recipe_title}
          className="h-16 w-16 rounded-lg object-cover"
        />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-sm line-clamp-2 mb-1">
          {favorite.recipe_title}
        </h3>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {favorite.ready_in_minutes && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {favorite.ready_in_minutes} min
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      {!selectMode && (
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Secondary: Add Ingredients */}
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-muted-foreground"
            onClick={onAddIngredients}
            data-testid={`add-ingredients-${favorite.recipe_id}`}
          >
            <ShoppingCart className="h-4 w-4" />
          </Button>

          {/* Secondary: View */}
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-muted-foreground"
            onClick={onView}
            data-testid={`view-recipe-${favorite.recipe_id}`}
          >
            <Eye className="h-4 w-4" />
          </Button>

          {/* Primary: Add to Plan */}
          <Button
            size="sm"
            className="h-9 px-3 bg-primary"
            onClick={onAddToPlan}
            data-testid={`add-to-plan-${favorite.recipe_id}`}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
      )}
    </div>
  );
};

export default MobileFavorites;
